import type { CircuitDTO, CircuitIR, InterfacePort } from "@models/circuit-ir";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMemo, useState } from "react";
import { useVersionIr } from "../../hooks/useCircuitIr";
/**
 * MappingBench — the marquee surface for merging a candidate Circuit
 * into a target main (IR plan §8.6 / Phase 14
 * `POST /circuits/{tgt}/merge`).
 *
 * V1 scope (this component):
 *   * Pick a source candidate Circuit + a target main Circuit.
 *   * Show the source's `Module.interface_ports` as a list of named
 *     external ports (`snubber.IN`, `snubber.OUT`, `snubber.GND`).
 *   * For each source port, pick a target net from the target IR via
 *     a searchable autocomplete.
 *   * Mapping summary shows the assembled pairs.
 *   * Submit → fires `useMergeCircuits` with the pairs.
 *
 * Deferred (V2):
 *   * Side-by-side schematic mini-canvases with cyan trail lines from
 *     source ports to target nets. The list-based picker covers the
 *     interaction; the canvas would add richness once we have the
 *     time to drive the drag-drop hit testing.
 *   * Refdes annotation preview before commit. Backend's merge_circuit
 *     returns the `refdes_remap` post-merge; surfacing it pre-flight
 *     needs a dry-run endpoint that doesn't exist yet.
 *   * DRC dry-run readout — same pre-flight requirement.
 */
import { useCircuit, useMergeCircuits } from "../../hooks/useCircuitLifecycle";

interface MappingBenchProps {
  open: boolean;
  /** Source candidate that will fold into the target. */
  sourceCircuit: CircuitDTO | null;
  /** Target main board the source merges into. */
  targetCircuit: CircuitDTO | null;
  onClose: () => void;
  onMerged?: (versionId: string) => void;
}

interface SourcePortRow {
  /** Module.name + InterfacePort.name — "snubber.IN" — the canonical
   *  source-port identifier the backend's merge_circuit expects. */
  qualifiedName: string;
  port: InterfacePort;
  /** Net inside the source IR the port resolves to — surfaced as a
   *  hint in the picker so the user knows what they're mapping. */
  sourceNetName: string | null;
}

interface TargetPin {
  /** "U7.drain" — refdes.pin format, matches backend expectation. */
  qualifiedName: string;
  refdes: string;
  pin: string;
  /** Connected net (if any) — surfaced as the picker's caption. */
  netName: string | null;
}

export function MappingBench({
  open,
  sourceCircuit,
  targetCircuit,
  onClose,
  onMerged,
}: MappingBenchProps) {
  // Body mounts/unmounts with `open` (MUI Dialog default: no keepMounted)
  // so mapping/archiveSource/description state resets naturally on each
  // open — no explicit reset useEffect required.
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <MappingBenchBody
        sourceCircuit={sourceCircuit}
        targetCircuit={targetCircuit}
        onClose={onClose}
        onMerged={onMerged}
      />
    </Dialog>
  );
}

interface MappingBenchBodyProps {
  sourceCircuit: CircuitDTO | null;
  targetCircuit: CircuitDTO | null;
  onClose: () => void;
  onMerged?: (versionId: string) => void;
}

function MappingBenchBody({
  sourceCircuit,
  targetCircuit,
  onClose,
  onMerged,
}: MappingBenchBodyProps) {
  const merge = useMergeCircuits();

  const sourceCircuitFull = useCircuit(sourceCircuit?.id ?? null).data;
  const targetCircuitFull = useCircuit(targetCircuit?.id ?? null).data;

  // Load the source + target IRs from their current_version_id. Both
  // requests dedupe via React Query — cheap to call here even though
  // other surfaces may have already fetched them.
  const sourceIrQ = useVersionIr(sourceCircuitFull?.current_version_id ?? null);
  const targetIrQ = useVersionIr(targetCircuitFull?.current_version_id ?? null);
  const sourceIr = sourceIrQ.data?.ir;
  const targetIr = targetIrQ.data?.ir;

  const sourcePorts = useMemo<SourcePortRow[]>(
    () => extractSourcePorts(sourceIr),
    [sourceIr],
  );
  const targetPins = useMemo<TargetPin[]>(
    () => extractTargetPins(targetIr),
    [targetIr],
  );

  // mapping[qualifiedSourcePort] = targetPin.qualifiedName
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [archiveSource, setArchiveSource] = useState(true);
  const [description, setDescription] = useState("");

  const allMapped =
    sourcePorts.length > 0 &&
    sourcePorts.every((p) => !!mapping[p.qualifiedName]);

  function handleMerge() {
    if (!targetCircuit?.id || !sourceCircuit?.id) return;
    merge.mutate(
      {
        targetCircuitId: targetCircuit.id,
        payload: {
          source_circuit_id: sourceCircuit.id,
          mapping: sourcePorts
            .filter((p) => !!mapping[p.qualifiedName])
            .map((p) => ({
              source_port: p.qualifiedName,
              target_pin: mapping[p.qualifiedName]!,
            })),
          archive_source: archiveSource,
          description: description.trim() || undefined,
        },
      },
      {
        onSuccess: (data) => {
          onMerged?.(data.version_id);
          onClose();
        },
      },
    );
  }

  const isLoading = sourceIrQ.isLoading || targetIrQ.isLoading;

  return (
    <>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack direction="row" alignItems="baseline" spacing={1.5}>
          <Box>Mapping Bench</Box>
          {sourceCircuit && targetCircuit && (
            <Typography variant="body2" color="text.secondary">
              {sourceCircuit.name} → {targetCircuit.name}
            </Typography>
          )}
        </Stack>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          Wire each external port on the source candidate to a pin on the target
          board. Refdes are reannotated against the target's used set on commit.
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {isLoading && (
          <Stack sx={{ py: 8, alignItems: "center" }}>
            <CircularProgress size={24} />
          </Stack>
        )}

        {!isLoading && (!sourceIr || !targetIr) && (
          <Alert severity="info" sx={{ m: 2 }}>
            Both circuits need a committed version before they can be merged.
          </Alert>
        )}

        {!isLoading && sourceIr && targetIr && sourcePorts.length === 0 && (
          <Alert severity="warning" sx={{ m: 2 }}>
            The source candidate has no `interface_ports` defined on its modules
            — there's nothing to map. Add named ports to the source's modules
            first.
          </Alert>
        )}

        {!isLoading && sourceIr && targetIr && sourcePorts.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 0,
              minHeight: 380,
            }}
          >
            <Pane title={`Source · ${sourceCircuit?.name}`} side="left">
              <Stack
                divider={<Divider sx={{ borderColor: "border.default" }} />}
              >
                {sourcePorts.map((p) => (
                  <SourcePortRow
                    key={p.qualifiedName}
                    port={p}
                    targetPins={targetPins}
                    pickedTargetPin={mapping[p.qualifiedName] ?? ""}
                    onPick={(target) =>
                      setMapping((m) => {
                        const next = { ...m };
                        if (target) next[p.qualifiedName] = target;
                        else delete next[p.qualifiedName];
                        return next;
                      })
                    }
                  />
                ))}
              </Stack>
            </Pane>
            <Pane title={`Target · ${targetCircuit?.name}`} side="right">
              <MappingSummary
                rows={sourcePorts}
                mapping={mapping}
                description={description}
                onDescriptionChange={setDescription}
                archiveSource={archiveSource}
                onArchiveChange={setArchiveSource}
              />
            </Pane>
          </Box>
        )}

        {merge.error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {(merge.error as Error).message ?? "Merge failed"}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Chip
            size="small"
            label={`${Object.keys(mapping).length}/${sourcePorts.length} mapped`}
            color={allMapped ? "success" : "warning"}
            variant="outlined"
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} color="inherit" disabled={merge.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!allMapped || merge.isPending}
            onClick={handleMerge}
          >
            {merge.isPending ? "Merging…" : "Merge"}
          </Button>
        </Stack>
      </DialogActions>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function Pane({
  title,
  side,
  children,
}: {
  title: string;
  side: "left" | "right";
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={(t) => ({
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        borderRight:
          side === "left" ? `1px solid ${t.palette.border.default}` : 0,
      })}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={(t) => ({
          px: 1.75,
          py: 1,
          borderBottom: `1px solid ${t.palette.border.default}`,
        })}
      >
        <Typography
          variant="overline"
          sx={(t) => ({
            color: t.palette.text.disabled,
            fontSize: 10,
            letterSpacing: "0.12em",
          })}
        >
          {title}
        </Typography>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 280, overflowY: "auto" }}>{children}</Box>
    </Box>
  );
}

function SourcePortRow({
  port,
  targetPins,
  pickedTargetPin,
  onPick,
}: {
  port: SourcePortRow;
  targetPins: TargetPin[];
  pickedTargetPin: string;
  onPick: (qualifiedTargetPin: string) => void;
}) {
  const value =
    targetPins.find((t) => t.qualifiedName === pickedTargetPin) ?? null;
  return (
    <Stack
      spacing={0.75}
      sx={(t) => ({
        p: 1.5,
        // Phosphor side-bar on a mapped row — visual confirmation
        // without the user re-reading the chip count.
        borderLeft: `2px solid ${
          pickedTargetPin ? t.palette.accent.main : "transparent"
        }`,
      })}
    >
      <Stack direction="row" spacing={1} alignItems="baseline">
        <Box
          sx={(t) => ({
            fontFamily: t.fonts.mono,
            fontSize: 12,
            color: "text.primary",
            fontWeight: 600,
          })}
        >
          {port.qualifiedName}
        </Box>
        <Box
          sx={(t) => ({
            fontFamily: t.fonts.mono,
            fontSize: 10,
            color: t.palette.editorial.main,
            textTransform: "uppercase",
          })}
        >
          {port.port.direction}
        </Box>
        {port.sourceNetName && (
          <Box
            sx={(t) => ({
              fontFamily: t.fonts.mono,
              fontSize: 10,
              color: "text.disabled",
            })}
          >
            · net {port.sourceNetName}
          </Box>
        )}
      </Stack>
      <Autocomplete
        options={targetPins}
        value={value}
        onChange={(_, next) => onPick(next?.qualifiedName ?? "")}
        getOptionLabel={(opt) => opt.qualifiedName}
        isOptionEqualToValue={(a, b) => a.qualifiedName === b.qualifiedName}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Pick a target pin (refdes.pin)"
            size="small"
          />
        )}
        renderOption={(props, opt) => (
          <Box component="li" {...props}>
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
              <Box
                sx={(t) => ({
                  fontFamily: t.fonts.mono,
                  fontSize: 12,
                  color: "text.primary",
                  fontWeight: 600,
                })}
              >
                {opt.qualifiedName}
              </Box>
              {opt.netName && (
                <Box
                  sx={(t) => ({
                    fontFamily: t.fonts.mono,
                    fontSize: 10,
                    color: "text.disabled",
                  })}
                >
                  on net {opt.netName}
                </Box>
              )}
            </Stack>
          </Box>
        )}
      />
    </Stack>
  );
}

function MappingSummary({
  rows,
  mapping,
  description,
  onDescriptionChange,
  archiveSource,
  onArchiveChange,
}: {
  rows: SourcePortRow[];
  mapping: Record<string, string>;
  description: string;
  onDescriptionChange: (v: string) => void;
  archiveSource: boolean;
  onArchiveChange: (v: boolean) => void;
}) {
  const mappedRows = rows.filter((r) => !!mapping[r.qualifiedName]);
  const unmappedRows = rows.filter((r) => !mapping[r.qualifiedName]);

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Box>
        <Typography
          variant="overline"
          sx={(t) => ({
            color: t.palette.editorial.main,
            fontSize: 10,
            letterSpacing: "0.16em",
          })}
        >
          MAPPED · {mappedRows.length}
        </Typography>
        {mappedRows.length === 0 ? (
          <Typography
            variant="caption"
            sx={{ color: "text.disabled", display: "block", mt: 0.5 }}
          >
            Pick target pins on the left to assemble the mapping.
          </Typography>
        ) : (
          <Stack
            spacing={0.5}
            sx={(t) => ({
              mt: 0.75,
              p: 1.25,
              borderRadius: 1,
              bgcolor: alpha(t.palette.accent.main, 0.06),
              border: `1px solid ${alpha(t.palette.accent.main, 0.25)}`,
            })}
          >
            {mappedRows.map((r) => (
              <Stack
                key={r.qualifiedName}
                direction="row"
                spacing={1}
                alignItems="baseline"
                sx={(t) => ({
                  fontFamily: t.fonts.mono,
                  fontSize: 11,
                })}
              >
                <Box sx={{ color: "primary.main", fontWeight: 600 }}>
                  {r.qualifiedName}
                </Box>
                <Box sx={{ color: "text.disabled" }}>→</Box>
                <Box sx={{ color: "primary.light", fontWeight: 600 }}>
                  {mapping[r.qualifiedName]}
                </Box>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>

      {unmappedRows.length > 0 && (
        <Box>
          <Typography
            variant="overline"
            sx={{
              color: "warning.main",
              fontSize: 10,
              letterSpacing: "0.16em",
            }}
          >
            UNMAPPED · {unmappedRows.length}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
            {unmappedRows.map((r) => (
              <Chip
                key={r.qualifiedName}
                size="small"
                label={r.qualifiedName}
                color="warning"
                variant="outlined"
                sx={{ fontFamily: "mono", fontSize: 10 }}
              />
            ))}
          </Stack>
        </Box>
      )}

      <TextField
        label="Description (optional)"
        value={description}
        onChange={(e) => onDescriptionChange(e.target.value)}
        multiline
        minRows={2}
        size="small"
        placeholder="Why is this candidate landing in main?"
      />

      <Stack direction="row" spacing={1} alignItems="center">
        <input
          id="mb-archive-src"
          type="checkbox"
          checked={archiveSource}
          onChange={(e) => onArchiveChange(e.target.checked)}
        />
        <Typography
          component="label"
          htmlFor="mb-archive-src"
          variant="caption"
          sx={{ color: "text.secondary" }}
        >
          Archive the source candidate after merge
        </Typography>
      </Stack>
    </Stack>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// IR walks
// ────────────────────────────────────────────────────────────────────────────

function extractSourcePorts(ir: CircuitIR | undefined): SourcePortRow[] {
  if (!ir) return [];
  const netById = new Map(ir.nets.map((n) => [n.id, n.name]));
  const out: SourcePortRow[] = [];
  for (const m of ir.modules ?? []) {
    for (const p of m.interface_ports ?? []) {
      out.push({
        qualifiedName: `${m.name}.${p.name}`,
        port: p,
        sourceNetName: netById.get(p.net_id) ?? null,
      });
    }
  }
  return out;
}

function extractTargetPins(ir: CircuitIR | undefined): TargetPin[] {
  if (!ir) return [];
  const refdesById = new Map(ir.components.map((c) => [c.id, c.refdes]));
  // Build a lookup: (component_id, pin_number) → first net it touches
  // (a pin can technically be on multiple nets via redundant endpoints
  // but the typical case is one).
  const netByEp = new Map<string, string | null>();
  for (const net of ir.nets) {
    for (const ep of net.endpoints) {
      const key = `${ep.component_id}::${ep.pin_number}`;
      if (!netByEp.has(key)) netByEp.set(key, net.name);
    }
  }
  const out: TargetPin[] = [];
  for (const c of ir.components) {
    for (const pin of c.pins) {
      const key = `${c.id}::${pin.number}`;
      out.push({
        qualifiedName: `${refdesById.get(c.id) ?? c.id.slice(0, 6)}.${pin.number}`,
        refdes: refdesById.get(c.id) ?? "",
        pin: pin.number,
        netName: netByEp.get(key) ?? null,
      });
    }
  }
  return out;
}
